"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  MessageSquare,
  X,
} from "lucide-react";

type ToastType = "success" | "error" | "warning" | "info" | "brand";

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast = { ...toast, id };

    setToasts((prev) => [...prev, newToast]);

    // Auto remove after duration
    const duration = toast.duration ?? 5000;
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

function ToastContainer() {
  const { toasts, removeToast } = useToast();

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      aria-label="Bildirimler"
      className="fixed bottom-[calc(84px+env(safe-area-inset-bottom,16px))] left-4 right-4 z-[100] flex flex-col gap-2 lg:bottom-4 lg:left-auto lg:max-w-sm"
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
}

interface ToastItemProps {
  toast: Toast;
  onClose: () => void;
}

const toastStyles: Record<
  ToastType,
  { icon: typeof CheckCircle; iconBox: string }
> = {
  success: {
    icon: CheckCircle,
    iconBox: "bg-success-glow-12 border-success-glow-20 text-success-light",
  },
  error: {
    icon: XCircle,
    iconBox: "bg-error-glow-12 border-error-glow-20 text-error-light",
  },
  warning: {
    icon: AlertCircle,
    iconBox: "bg-warning-glow-12 border-warning-glow-20 text-warning-light",
  },
  info: {
    icon: Info,
    iconBox: "bg-info-glow-12 border-info-glow-20 text-info-light",
  },
  brand: {
    icon: MessageSquare,
    iconBox: "bg-brand-glow-12 border-brand-glow-20 text-brand-light",
  },
};

function ToastItem({ toast, onClose }: ToastItemProps) {
  const { icon: Icon, iconBox } = toastStyles[toast.type];

  return (
    <div
      className={`
        flex items-start gap-3 p-3.5
        bg-card border border-border rounded-2xl [box-shadow:var(--toast-shadow)]
        animate-[slide-up_300ms_ease-out]
      `}
      role="alert"
    >
      <span
        className={`flex-shrink-0 inline-flex items-center justify-center w-[34px] h-[34px] rounded-[11px] border ${iconBox}`}
      >
        <Icon className="w-[18px] h-[18px]" />
      </span>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground leading-[1.4]">
          {toast.title}
        </p>
        {toast.message && (
          <p className="mt-0.5 text-[13px] text-muted-foreground leading-[1.5]">
            {toast.message}
          </p>
        )}
      </div>

      <button
        onClick={onClose}
        className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md text-muted hover:text-foreground hover:bg-card-hover transition-colors"
        aria-label="Kapat"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// Convenience hooks for common toast types
export function useSuccessToast() {
  const { addToast } = useToast();
  return useCallback(
    (title: string, message?: string) => {
      addToast({ type: "success", title, message });
    },
    [addToast],
  );
}

export function useErrorToast() {
  const { addToast } = useToast();
  return useCallback(
    (title: string, message?: string) => {
      addToast({ type: "error", title, message });
    },
    [addToast],
  );
}
